/**
 * Werte-Kartenspiel — the deterministic reduction machine (W1 redesign §3, V2).
 *
 * A bespoke, PURE, seeded state machine that ports the proven 9-step reduction
 * (`orgs/uneq/dna-workshop/values/personal-values.md`, concepts only) to the career
 * domain: shuffle 62 → pairwise »Was ist dir wichtiger: A oder B?« down to 8 →
 * optional bounded rescue/swap (»Eventuell retten«) → pick 1 Lebenswert → pick 3
 * Superkräfte (the remaining 4 are Basiswerte).
 *
 * PERMANENTLY AI-FREE (decisions Q7): there is NO model call, NO hint, NO flag —
 * anywhere. The pyramid is the one artifact guaranteed free of AI influence: the
 * user's own recognition, full stop. Every function here is pure and total; the seed
 * fully determines the shuffle, so a resume on another device continues identically.
 *
 * The snapshot shape is the domain type `ValueGameSnapshot` (workshop.domain.ts) — the
 * `ValueGameProgressed` LWW resume payload; this module is the ONE reducer over it, so
 * the client and the tests share one implementation (no forked game logic).
 */

import { VALUE_CARD_CATALOG, valueCardById, type ValueCard } from "./value-card-catalog"
import type { ValueGameSnapshot, ValuePyramid, ValuePyramidSlot } from "./inferred"

/** The pyramid shape is fixed (§9): 1 Lebenswert · 3 Superkräfte · 4 Basiswerte. */
export const PYRAMID_SHAPE = { lebenswert: 1, superkraefte: 3, basiswerte: 4 } as const
/** The reduction target — the deck shrinks to exactly this many cards before the pyramid. */
export const PYRAMID_SIZE = PYRAMID_SHAPE.lebenswert + PYRAMID_SHAPE.superkraefte + PYRAMID_SHAPE.basiswerte
/** Swaps allowed in the refine stage (bounded »rescue/swap« budget, §3). */
export const REFINE_BUDGET = 3

// ─── Deterministic PRNG (seed → shuffle) ─────────────────────────────────────────
//
// A tiny, dependency-free, fully deterministic PRNG. `hashSeed` folds the string seed
// into a 32-bit integer (xfnv1a); `mulberry32` turns that into a uniform stream. Same
// seed ⇒ same shuffle ⇒ resumable on any device with no stored deck.

function hashSeed(seed: string): number {
  let h = 2166136261 >>> 0
  for (let i = 0; i < seed.length; i++) {
    h = Math.imul(h ^ seed.charCodeAt(i), 16777619)
  }
  return h >>> 0
}

function mulberry32(a: number): () => number {
  return () => {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** Deterministic Fisher-Yates over a COPY (seeded) — a pure permutation. */
export function seededShuffle<T>(items: readonly T[], seed: string): T[] {
  const out = items.slice()
  const rand = mulberry32(hashSeed(seed))
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1))
    ;[out[i], out[j]] = [out[j]!, out[i]!]
  }
  return out
}

// ─── Snapshot construction ───────────────────────────────────────────────────────

/** All catalog card ids, in catalog order (the reduction operates on ids). */
const ALL_CARD_IDS: readonly string[] = VALUE_CARD_CATALOG.map((c) => c.cardId)

/** Start a fresh game — a seeded shuffle of the whole deck; phase `reduce`, round 0. */
export function newValueGame(seed: string, now: string): ValueGameSnapshot {
  const order = seededShuffle(ALL_CARD_IDS, seed)
  return {
    seed,
    phase: "reduce",
    order,
    keep: order.slice(),
    out: [],
    maybe: [],
    roundWinners: [],
    pairIndex: 0,
    round: 0,
    rescueBudget: REFINE_BUDGET,
    lebenswert: null,
    superkraefte: [],
    updatedAt: now,
  }
}

// ─── Reduce phase — pairwise »Was ist dir wichtiger?« ─────────────────────────────

/** The current pair the user decides (or null when not reducing / round exhausted). */
export interface ValuePair {
  a: ValueCard
  b: ValueCard
}

/** How many decidable pairs the current round holds (an odd tail card gets a bye). */
export function roundPairCount(snap: ValueGameSnapshot): number {
  return Math.floor(snap.keep.length / 2)
}

/** The pair at the current cursor, resolved to catalog cards — null if not in `reduce`
 *  or the round's pairs are exhausted. */
export function currentPair(snap: ValueGameSnapshot): ValuePair | null {
  if (snap.phase !== "reduce") return null
  if (snap.pairIndex >= roundPairCount(snap)) return null
  const a = valueCardById(snap.keep[snap.pairIndex * 2] ?? "")
  const b = valueCardById(snap.keep[snap.pairIndex * 2 + 1] ?? "")
  return a && b ? { a, b } : null
}

/**
 * Decide the current pair: the winner survives to `roundWinners`; the loser goes to
 * `out` (confident) or `maybe` (`unsure` → »Eventuell retten«, rescue-eligible). When
 * the round's pairs are exhausted, an odd tail card advances on a bye, `keep` becomes
 * the winners, and the next round begins — until the deck reaches PYRAMID_SIZE, at
 * which point the phase advances to `refine`. Total & pure: a call outside `reduce`,
 * or with no current pair, returns the snapshot unchanged.
 */
export function chooseWinner(
  snap: ValueGameSnapshot,
  winner: "a" | "b",
  unsure: boolean,
  now: string,
): ValueGameSnapshot {
  const pair = currentPair(snap)
  if (!pair) return snap
  const keptId = winner === "a" ? pair.a.cardId : pair.b.cardId
  const lostId = winner === "a" ? pair.b.cardId : pair.a.cardId

  const roundWinners = [...snap.roundWinners, keptId]
  const out = unsure ? snap.out : [...snap.out, lostId]
  const maybe = unsure ? [...snap.maybe, lostId] : snap.maybe
  const pairIndex = snap.pairIndex + 1

  const next: ValueGameSnapshot = { ...snap, roundWinners, out, maybe, pairIndex, updatedAt: now }

  // Round not yet exhausted → keep decidng pairs.
  if (pairIndex < roundPairCount(snap)) return next

  // Round exhausted: an odd tail card advances on a bye.
  const finishedWinners =
    snap.keep.length % 2 === 1 ? [...roundWinners, snap.keep[snap.keep.length - 1]!] : roundWinners

  // Reached the pyramid size → hand off to the refine stage (bounded rescue/swap).
  if (finishedWinners.length <= PYRAMID_SIZE) {
    return { ...next, keep: finishedWinners, roundWinners: [], pairIndex: 0, phase: "refine", updatedAt: now }
  }
  // Otherwise start the next reduction round on the winners.
  return { ...next, keep: finishedWinners, roundWinners: [], pairIndex: 0, round: snap.round + 1, updatedAt: now }
}

// ─── Refine phase — bounded rescue/swap (»Eventuell retten«) ──────────────────────

/** The cards eligible to be rescued back in the refine stage (maybe-pile first, then
 *  the hard-out pile) — resolved to catalog cards, deduped, and never already kept. */
export function rescueCandidates(snap: ValueGameSnapshot): ValueCard[] {
  const kept = new Set(snap.keep)
  const seen = new Set<string>()
  const ids = [...snap.maybe, ...snap.out]
  const out: ValueCard[] = []
  for (const id of ids) {
    if (kept.has(id) || seen.has(id)) continue
    seen.add(id)
    const card = valueCardById(id)
    if (card) out.push(card)
  }
  return out
}

/**
 * The cards the user EXPLICITLY marked »unsicher« during the reduction (the `maybe`
 * pile) — resolved to catalog cards, deduped, and never one already kept. These are
 * the promised »wir kommen darauf zurück« reminder: in the refine stage they are THE
 * exchange candidates, so an unsure mark actually surfaces later. Empty when the user
 * was never unsure — the caller then falls back to {@link rescueCandidates}.
 */
export function unsureCandidates(snap: ValueGameSnapshot): ValueCard[] {
  const kept = new Set(snap.keep)
  const seen = new Set<string>()
  const out: ValueCard[] = []
  for (const id of snap.maybe) {
    if (kept.has(id) || seen.has(id)) continue
    seen.add(id)
    const card = valueCardById(id)
    if (card) out.push(card)
  }
  return out
}

/**
 * Swap a kept card for a rescued one (refine stage). Costs one rescue-budget unit; a
 * no-op (returns the snapshot unchanged) when the budget is spent, the ids are unknown,
 * `rescueId` is already kept, or `dropId` is not currently kept. The dropped card
 * returns to `out`; the rescued card leaves the maybe/out piles.
 */
export function swapCard(snap: ValueGameSnapshot, rescueId: string, dropId: string, now: string): ValueGameSnapshot {
  if (snap.phase !== "refine" || snap.rescueBudget <= 0) return snap
  if (!snap.keep.includes(dropId) || snap.keep.includes(rescueId)) return snap
  if (!valueCardById(rescueId) || !valueCardById(dropId)) return snap
  if (!snap.maybe.includes(rescueId) && !snap.out.includes(rescueId)) return snap

  const keep = snap.keep.map((id) => (id === dropId ? rescueId : id))
  const maybe = snap.maybe.filter((id) => id !== rescueId)
  const out = [...snap.out.filter((id) => id !== rescueId), dropId]
  return { ...snap, keep, maybe, out, rescueBudget: snap.rescueBudget - 1, updatedAt: now }
}

/** Leave the refine stage and begin picking the Lebenswert. */
export function finishRefine(snap: ValueGameSnapshot, now: string): ValueGameSnapshot {
  if (snap.phase !== "refine") return snap
  return { ...snap, phase: "lebenswert", updatedAt: now }
}

// ─── Pyramid phases — 1 Lebenswert → 3 Superkräfte → confirm ───────────────────────

/** The 8 kept cards, resolved to catalog cards (the pyramid pool). */
export function keptCards(snap: ValueGameSnapshot): ValueCard[] {
  return snap.keep.map((id) => valueCardById(id)).filter((c): c is ValueCard => Boolean(c))
}

/** Pick (or re-pick) the single Lebenswert — must be one of the kept 8. */
export function pickLebenswert(snap: ValueGameSnapshot, cardId: string, now: string): ValueGameSnapshot {
  if (snap.phase !== "lebenswert" || !snap.keep.includes(cardId)) return snap
  // Re-picking the Lebenswert frees any Superkraft that collides with the new choice.
  const superkraefte = snap.superkraefte.filter((id) => id !== cardId)
  return { ...snap, lebenswert: cardId, superkraefte, phase: "superkraefte", updatedAt: now }
}

/** Toggle a Superkraft (max 3) — must be kept and not the Lebenswert. Selecting a 4th
 *  is a no-op; deselecting always works. */
export function toggleSuperkraft(snap: ValueGameSnapshot, cardId: string, now: string): ValueGameSnapshot {
  if (snap.phase !== "superkraefte" || !snap.keep.includes(cardId) || cardId === snap.lebenswert) return snap
  const has = snap.superkraefte.includes(cardId)
  if (!has && snap.superkraefte.length >= PYRAMID_SHAPE.superkraefte) return snap
  const superkraefte = has ? snap.superkraefte.filter((id) => id !== cardId) : [...snap.superkraefte, cardId]
  return { ...snap, superkraefte, updatedAt: now }
}

/** Advance to the confirm phase — requires exactly 3 Superkräfte and a Lebenswert. */
export function confirmSuperkraefte(snap: ValueGameSnapshot, now: string): ValueGameSnapshot {
  if (snap.phase !== "superkraefte") return snap
  if (!snap.lebenswert || snap.superkraefte.length !== PYRAMID_SHAPE.superkraefte) return snap
  return { ...snap, phase: "confirm", updatedAt: now }
}

/** Step back to Superkräfte from confirm (edit before commit). */
export function backToSuperkraefte(snap: ValueGameSnapshot, now: string): ValueGameSnapshot {
  if (snap.phase !== "confirm") return snap
  return { ...snap, phase: "superkraefte", updatedAt: now }
}

// ─── Commit — assemble the 1·3·4 pyramid ──────────────────────────────────────────

/** The 4 Basiswerte = the kept cards that are neither the Lebenswert nor a Superkraft. */
export function basiswerteIds(snap: ValueGameSnapshot): string[] {
  const top = new Set<string>([snap.lebenswert ?? "", ...snap.superkraefte])
  return snap.keep.filter((id) => !top.has(id))
}

/**
 * Assemble the committed `ValuePyramid` from a snapshot in the `confirm` phase.
 * Throws when the snapshot cannot form a well-shaped pyramid (wrong pool size, no
 * Lebenswert, not exactly 3 Superkräfte, or an unknown card) — the caller commits
 * only a valid pyramid. Slot order is Lebenswert → Superkräfte → Basiswerte.
 */
export function buildPyramid(snap: ValueGameSnapshot, formedAt: string): ValuePyramid {
  const err = pyramidReadiness(snap)
  if (err) throw new Error(`cannot form pyramid: ${err}`)
  const slot = (cardId: string, tier: ValuePyramidSlot["tier"]): ValuePyramidSlot => {
    const card = valueCardById(cardId)!
    return { cardId, name: card.name, tier }
  }
  const slots: ValuePyramidSlot[] = [
    slot(snap.lebenswert!, "Lebenswert"),
    ...snap.superkraefte.map((id) => slot(id, "Superkräfte")),
    ...basiswerteIds(snap).map((id) => slot(id, "Basiswerte")),
  ]
  return { slots, formedAt }
}

/** Why the snapshot cannot yet commit a pyramid — a short reason, or null when ready. */
export function pyramidReadiness(snap: ValueGameSnapshot): string | null {
  if (snap.keep.length !== PYRAMID_SIZE) return `deck must hold ${PYRAMID_SIZE} cards, has ${snap.keep.length}`
  if (!snap.lebenswert || !snap.keep.includes(snap.lebenswert)) return "no Lebenswert chosen"
  if (snap.superkraefte.length !== PYRAMID_SHAPE.superkraefte) return "exactly 3 Superkräfte required"
  if (snap.superkraefte.some((id) => !snap.keep.includes(id) || id === snap.lebenswert)) return "invalid Superkraft"
  if (new Set(snap.keep).size !== PYRAMID_SIZE) return "duplicate cards in the deck"
  if (snap.keep.some((id) => !valueCardById(id))) return "unknown card in the deck"
  return null
}

// ─── Server-side validation (the client is an owner-scoped trust boundary) ─────────

/**
 * Validate a `ValuePyramid` the client committed — independent of the client reducer
 * (owner-scoping is the security boundary, but the SHAPE is still checked): exactly
 * 1·3·4 tiers, every `cardId` in the frozen catalog, no duplicates. Returns a reason
 * or null. The api asserts the ArkType shape first, then this structural law.
 */
export function validateCommittedPyramid(pyramid: ValuePyramid): string | null {
  const { slots } = pyramid
  if (slots.length !== PYRAMID_SIZE) return `pyramid must hold ${PYRAMID_SIZE} slots, has ${slots.length}`
  const byTier = { Lebenswert: 0, Superkräfte: 0, Basiswerte: 0 }
  const ids = new Set<string>()
  for (const s of slots) {
    byTier[s.tier] += 1
    ids.add(s.cardId)
    const card = valueCardById(s.cardId)
    if (!card) return `unknown cardId: ${s.cardId}`
    if (card.name !== s.name) return `cardId/name mismatch: ${s.cardId}`
  }
  if (ids.size !== PYRAMID_SIZE) return "duplicate cardId in pyramid"
  if (byTier.Lebenswert !== PYRAMID_SHAPE.lebenswert) return "exactly 1 Lebenswert required"
  if (byTier.Superkräfte !== PYRAMID_SHAPE.superkraefte) return "exactly 3 Superkräfte required"
  if (byTier.Basiswerte !== PYRAMID_SHAPE.basiswerte) return "exactly 4 Basiswerte required"
  return null
}

/** Progress read for the UI — how far the reduction has come (0 at start, 1 at 8 cards). */
export function reduceProgress(snap: ValueGameSnapshot): number {
  if (snap.phase !== "reduce") return 1
  const start = snap.order.length
  const span = start - PYRAMID_SIZE
  if (span <= 0) return 1
  return Math.min(1, Math.max(0, (start - snap.keep.length) / span))
}
